/**
 * Production Manifest — the per-garment production contract (BLU-823).
 *
 * Exported from `packages/shared/src/index.ts` and wired end-to-end (Phases
 * 1-5, v1: custom POD, DTG station only — see `apps/backend/src/lib/
 * production-manifest/`). `build.ts` writes it after `createGarmentUnits`;
 * `read.ts` applies the trust gate; `divergence.ts` compares it to a live
 * probe when the location's read flag is off; `invalidate.ts` deletes/
 * rebuilds it on print-file-issue resolution and order changes.
 *
 * Derived from what production actually reads today: the DTG station
 * (`print-station.dao.ts` `getGarmentForPrint`), the DTF station
 * (`film-print.service.ts` `prepareFilm`), and the agent resolve route
 * (`routers/print-agent.ts` `handleGetPrintFile`).
 *
 * Two invariants the TYPE enforces, because the code could not:
 *
 * 1. EXPECTATION IS NOT PROOF. `print_file_lookups` stores only the expected
 *    filename; nothing records that anyone ever saw the object. That gap is the
 *    whole reason the live cross-Pacific probe exists. Every `ManifestFile`
 *    carries `expected` AND a separate `proof`, and a `found` proof carries
 *    WHERE it was seen (bucket + region) and WHEN.
 *
 * 2. INDETERMINATE IS NOT ABSENT. A stalled probe must never read as "this side
 *    doesn't exist" — that collapse is the half-printed dual-sided garment
 *    fixed in BLU-820 (`lib/storage/custom-sides.ts`, and the DTF
 *    `FILM_ARTWORK_TRANSIENT` branch). The proof union is discriminated;
 *    `indeterminate` has NO `verifiedAt`/`verifiedIn` fields at all (it carries
 *    `attemptedAt`/`attemptedIn`), so `.verifiedAt` cannot be read off it. Side
 *    presence is reachable only via `sidePresence()`, which is tri-state.
 *
 * NEVER store a presigned URL here. Those are 5-minute credentials (the agent
 * route's `expiresAt`, `DROP_URL_EXPIRY_SECONDS`); a stored one is a dead link
 * and a leak.
 */
import { z } from 'zod';
/** Bump on ANY breaking change. Readers MUST reject an unknown version rather
 *  than best-effort parse — a manifest is a safety claim, not a cache.
 *
 *  v2 (BLU-877): a side proves its file PER FORMAT (`files`, was `file`). Every
 *  stored v1 row fails `z.literal(2)`, reads as `parse_failed`, gates untrusted
 *  and falls back to a live probe. That is fail-SAFE, not fail-open: the
 *  fallback is the pre-manifest behaviour, so the cost of the bump is bounded
 *  latency on a flipped floor, never a wrong presence verdict. */
export const PRODUCTION_MANIFEST_VERSION = 2;
/** Where a proof was taken. Not "which org" — which physical bucket. */
export const storageLocusSchema = z.object({
    backend: z.enum(['gcs', 'tencent_cos', 'azure']),
    /** '' when the backend has no bucket concept (azure containers). */
    bucketName: z.string(),
    /** Tencent COS reads Shanghai / writes Singapore — a proof taken in one is
     *  NOT a proof about the other. Null when the backend has no region here. */
    region: z.string().nullable(),
    /** Per-tenant prefix (`org_<owner>` or `org_<owner>/clients/<client>`). */
    prefix: z.string(),
    /** `customsFolder` or `printsFolder` as resolved by `storage/factory.ts`. */
    folder: z.string(),
});
/** All the identity either adapter exposes — `{size, mtime}`. No ETag exists on
 *  GCS or COS through Flystorage (`lib/storage/object-identity.ts`). Do not
 *  claim content identity from this. */
export const objectIdentitySchema = z.object({
    sizeBytes: z.number().int().nonnegative().nullable(),
    mtimeMs: z.number().int().nonnegative().nullable(),
});
export const indeterminateCauseSchema = z.enum([
    'timeout', // bounded probe hit its deadline — UNREACHABLE, not absent
    'credential', // 401/403 — a Vault/CAM incident, sticky across both sides
    'transient', // 5xx / network
    'unsupported', // backend cannot probe (azure has no fileExists)
    'not_probed', // resolution failed before any probe ran
]);
/**
 * Proof of presence. Discriminated so `indeterminate` structurally cannot
 * expose a verification timestamp or locus.
 */
export const presenceProofSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('found'),
        verifiedAt: z.string().datetime(),
        verifiedIn: storageLocusSchema,
        identity: objectIdentitySchema,
    }),
    z.object({
        status: z.literal('absent'),
        verifiedAt: z.string().datetime(),
        verifiedIn: storageLocusSchema,
    }),
    z.object({
        status: z.literal('indeterminate'),
        attemptedAt: z.string().datetime(),
        attemptedIn: storageLocusSchema,
        cause: indeterminateCauseSchema,
    }),
]);
/** How the EXPECTED filename was arrived at. Not decorative: a `lookup_dual`
 *  name comes from the inverted column contract (`resolve-film-sides.ts` —
 *  `dual_back_filename` holds the FRONT file), and a `url_fallback` name comes
 *  from the order item's artwork URL, which self-repair can rewrite. */
export const filenameOriginSchema = z.enum([
    'lookup', // print_file_lookups.print_filename
    'lookup_dual', // print_file_lookups.dual_back_filename (INVERTED)
    'sku_grammar', // parseSKU + toPrintFile fallback
    'custom_stem_probe', // buildCustomStem + resolvePwCustomFile
    'url_fallback', // order_items.artwork_{front,back}_url basename
]);
/** Which candidate-extension policy produced this filename. A DTG manifest
 *  (`.arx4` preferred — a Brother device contract) is NOT usable by the DTF
 *  compositor, which cannot decode `.arx4` at all, and vice versa. */
export const extensionPolicySchema = z.enum(['prefer_arx4', 'raster_only']);
export const manifestFileSchema = z.object({
    expected: z.object({
        filename: z.string().min(1).max(255).regex(/^[A-Za-z0-9._-]+$/),
        /** Full object key, prefix+folder+filename already joined. */
        storageKey: z.string().min(1),
        origin: filenameOriginSchema,
        extensionPolicy: extensionPolicySchema,
    }),
    proof: presenceProofSchema,
});
/** One printable side. Absence of a `front`/`back` key on `sides` means the
 *  side was never applicable; a present key with an `absent` proof means it was
 *  applicable and is missing. Those are different facts — do not merge them. */
export const manifestSideSchema = z.object({
    /**
     * ONE PROOF PER PRINTER FORMAT (BLU-877, v2). The floor at ThreadHeads HQ
     * runs two device contracts at one location — a Brother GTX eats `.arx4`, a
     * Kora eats `.png` — so "this side is present" has no single answer and a
     * scalar `file` was a lie by omission the moment a Kora joined.
     *
     * THE DISTINCTION THE WHOLE SCHEMA TURNS ON, and the one thing never to
     * merge: a MISSING format key means that lane was NEVER PROBED — `unknown`,
     * which fails closed to a live probe. A PRESENT key carrying an `absent`
     * proof means the lane WAS probed and the file genuinely is not there. Those
     * are different facts. Collapsing them lets a Kora read a Brother-only
     * build's silence as either "covered" or "confirmed missing", and both of
     * those are verdicts nobody measured.
     *
     * Written as an explicit object rather than `z.record(printerFormatSchema,
     * ...)`: the record form's optionality semantics differ across zod minors,
     * and this shape is a safety claim, not a convenience.
     *
     * KEEP IN LOCKSTEP with `PRINTER_FORMATS` (`printer-format.ts`) — a third
     * format means a third key here, and a `sidePresence` that answers for it.
     */
    files: z.object({
        arx4: manifestFileSchema.optional(),
        png: manifestFileSchema.optional(),
    }),
    /** print_file_lookups.front_platen / back_platen (Shopify-metafield sourced). */
    platen: z.string().nullable(),
    /** Claimed pHYs density. Only knowable by READING the bytes, so null unless a
     *  byte-level pass (pre-mirror / compositor) ran. 300 is the DTF standard. */
    claimedDpi: z.number().int().positive().nullable(),
});
/** Facts ALREADY STORED on `prod_garment_units` — echoed so production reads
 *  ONE row. Never a second source of truth: the unit row wins on conflict. */
export const manifestGarmentFactsSchema = z.object({
    sku: z.string(),
    shopifySku: z.string().nullable(),
    productName: z.string(),
    variantName: z.string().nullable(),
    size: z.string().nullable(),
    color: z.string().nullable(),
    productionMethod: z.enum(['POD', 'PAP']),
    printTechnology: z.enum(['DTG', 'DTF']),
    /** Null when the source (`CreateGarmentUnitInput` at the batch path) never
     *  carries the column — do NOT coalesce to `false`; a fabricated `false`
     *  would read as "confirmed no pretreat needed" instead of "unknown". The
     *  invalidation/rebuild path (`invalidate.ts`) reads the real DB column. */
    requiresPretreat: z.boolean().nullable(),
    isRedo: z.boolean(),
    blankSkuId: z.string().uuid().nullable(),
    substitutedSkuId: z.string().uuid().nullable(),
});
/** DTF-only, and only after a byte-level pass. DTG needs none of this. */
export const manifestDtfFactsSchema = z.object({
    /** BOTH sides resolved → the film MUST carry a combined image or fail closed. */
    requiresCombinedFilm: z.boolean(),
    /** org_print_size_rules output, when a rule applied. */
    printSizeCm: z.object({ width: z.number(), height: z.number() }).nullable(),
    /** Detected effectively-blank artwork (opaque near-white) per side. */
    blankSides: z.array(z.enum(['front', 'back'])),
});
/**
 * Fingerprint of every input the manifest was derived from. Phase 5
 * invalidation compares these instead of relying only on events — several
 * mutators (a wholesale product-metadata-sync rewrite; an artwork-URL
 * self-repair) emit no per-garment event to hook.
 */
export const manifestInputsSchema = z.object({
    lookupRowUpdatedAt: z.string().datetime().nullable(),
    pwSnapshotId: z.string().nullable(),
    /** v1 writes null — URLs may carry signatures; only their hash enters
     *  storageConfigFingerprint. */
    artworkFrontUrl: z.string().nullable(),
    /** v1 writes null — URLs may carry signatures; only their hash enters
     *  storageConfigFingerprint. */
    artworkBackUrl: z.string().nullable(),
    /** sha256 of `{sku, pwSnapshotId, artworkFrontUrl, artworkBackUrl, locus}`
     *  (`computeInputsFingerprint`, `lib/production-manifest/fingerprint.ts`).
     *  Any change — a different SKU, a fresh PW snapshot, an artwork-URL
     *  self-repair rewrite, or a storage reconfig that moved the locus —
     *  invalidates every `storageKey` the manifest claims. Stage 2 (standards)
     *  adds `lookupRowUpdatedAt` (above) into this hash too; v1 leaves it
     *  reserved-but-unhashed since customs never populate it. */
    storageConfigFingerprint: z.string(),
});
export const productionManifestSchema = z.object({
    version: z.literal(PRODUCTION_MANIFEST_VERSION),
    resolvedAt: z.string().datetime(),
    garmentUnitId: z.string().uuid(),
    /** The garment/SKU OWNER org (client org for a 3PL line) — never the caller. */
    rowOrgId: z.string().uuid(),
    /** The order's ACTUAL fulfilment location. Storage is resolved against the
     *  LOCATION OWNER's config, which may be a different org than rowOrgId. */
    locationId: z.string().uuid(),
    prodOrderId: z.string().uuid(),
    orderItemId: z.string().uuid().nullable(),
    batchId: z.string().uuid().nullable(),
    garment: manifestGarmentFactsSchema,
    /** Where every proof in `sides` was taken. */
    locus: storageLocusSchema,
    /** Only applicable sides appear. Empty object = nothing resolvable. */
    sides: z.object({
        front: manifestSideSchema.optional(),
        back: manifestSideSchema.optional(),
    }),
    dtf: manifestDtfFactsSchema.nullable(),
    inputs: manifestInputsSchema,
});
// ---------------------------------------------------------------------------
// v1 -> v2 upgrade-on-read (BLU-877, decided 2026-09-04 by Brett).
//
// A v1 row differs from v2 in exactly ONE place: a side carried a scalar
// `file` instead of per-format `files`. The file it proved has an extension,
// and that extension IS the format lane it proved — so a v1 row can be lifted
// into a v2 row losslessly, with that one lane populated and every other lane
// left `undefined` (= never probed, fails closed to a live probe). Nothing is
// invented: a v1 row that proved `x.png` becomes a v2 row proving only the
// png lane, which is precisely what it measured.
//
// Why bother: the flip win at a flipped floor (Ningbo, 270 rows) would
// otherwise reset to live probes for up to 72h after the deploy. The upgrade
// keeps those rows serving until they age out naturally. The reader still
// checks a lifted row's FINGERPRINT the v1 way (`read.ts`), because v1 hashed
// the location's customs declaration where v2 hashes the fleet's format set.
//
// WHAT MAKES THAT ACTUALLY TRUE AT NINGBO (review WR-02). A lifted row proved
// exactly one lane, and the gate applies the STRICT every-lane fold to it, so
// it only survives if the CURRENT expectation names no lane the row never
// measured. Ningbo's expectation is `customsFormatsFor(['arx4'], …)` — an
// undeclared fleet at a mainland-COS floor declaring `customs: 'png'` — which
// is `['png']` ONLY because the Tencent impossible-format guard runs inside
// that derivation. Without it the expectation was `['arx4','png']`, the arx4
// lane was `unknown` on every lifted row, and all 270 were refused: the
// upgrade was dead at the one floor it was written for. If that guard is ever
// removed from `customsFormatsFor`, this paragraph and this whole path go with
// it — do not re-verify one without the other.
//
// DELETE WITH 84-08 (or any time >72h after the v2 deploy): once every stored
// row is v2 the legacy path is dead code, and the BLU-825 declaration it
// depends on is gone.
// ---------------------------------------------------------------------------
/** The v1 side shape: one scalar proof, no lane. */
const legacyManifestSideV1Schema = z.object({
    file: manifestFileSchema,
    platen: z.string().nullable(),
    claimedDpi: z.number().int().positive().nullable(),
});
/** A manifest exactly as v1 wrote it. Used ONLY by the upgrade; never trusted directly. */
export const legacyProductionManifestV1Schema = productionManifestSchema.extend({
    version: z.literal(1),
    sides: z.object({
        front: legacyManifestSideV1Schema.optional(),
        back: legacyManifestSideV1Schema.optional(),
    }),
});
/** The lane a v1 proof belongs to: its filename's extension, last-dot split.
 *  `null` when the extension is not a printer format — that row cannot be
 *  lifted and must fail closed. */
function laneForLegacyFile(filename) {
    const dot = filename.lastIndexOf('.');
    if (dot < 0)
        return null;
    const ext = filename.slice(dot + 1);
    return ext === 'arx4' || ext === 'png' ? ext : null;
}
function upgradeLegacySide(side) {
    const lane = laneForLegacyFile(side.file.expected.filename);
    if (lane === null)
        return null;
    return {
        files: { [lane]: side.file },
        platen: side.platen,
        claimedDpi: side.claimedDpi,
    };
}
/**
 * Lift a stored v1 manifest into v2 shape. Pure. Returns `null` when a side's
 * file has an extension that is not a printer format (the row cannot say which
 * lane it proved) — the caller treats that exactly like a parse failure.
 *
 * The returned object is re-validated against `productionManifestSchema` by
 * the reader, so this function's output is never trusted on its own.
 */
export function upgradeManifestV1(v1) {
    const sides = {};
    if (v1.sides.front) {
        const front = upgradeLegacySide(v1.sides.front);
        if (front === null)
            return null;
        sides.front = front;
    }
    if (v1.sides.back) {
        const back = upgradeLegacySide(v1.sides.back);
        if (back === null)
            return null;
        sides.back = back;
    }
    return { ...v1, version: PRODUCTION_MANIFEST_VERSION, sides };
}
/**
 * Tri-state by construction, and PER FORMAT (BLU-877). There is deliberately no
 * `hasFront: boolean` on the manifest — a boolean is where `unknown` gets
 * silently spent as `absent` — and deliberately no format-free overload: a
 * presence answer that does not name the format it is about is the exact lie
 * this schema version exists to end.
 *
 * - no side key at all      -> `absent` (the side was never applicable), as before
 * - side present, lane absent -> `unknown` (never probed in THIS format)
 * - lane present            -> its proof, mapped tri-state
 */
export function sidePresence(side, format) {
    if (!side)
        return 'absent';
    const file = side.files[format];
    if (!file)
        return 'unknown';
    switch (file.proof.status) {
        case 'found': return 'present';
        case 'absent': return 'absent';
        case 'indeterminate': return 'unknown';
    }
}
/**
 * AN EMPTY `formats` ARRAY IS A QUESTION NOBODY ASKED, and every fold below
 * answers it the fail-closed way (review WR-04).
 *
 * `formats` names the lanes a caller wants an answer about. Fold it over an
 * empty set and JavaScript's own identities take over: `[].some(...)` is
 * `false`, `[].map(...)` is `[]`, so `sideCoveragePresence` would return
 * `absent` for a side that is fully `found`, `deriveManifestStatus` would
 * return `ready` for a manifest that proved NOTHING, and
 * `manifestIsIndeterminate` would say a manifest nobody measured is settled.
 * That is precisely the schema's own contract inverted: "unknown must never
 * become absent, and absent must never become ready."
 *
 * `neededFormats` never returns an empty set and `customsFormatsFor` is written
 * so it cannot either — but these are exported shared-package functions taking
 * a bare `readonly PrinterFormat[]`, and one future caller filtering a set down
 * to nothing would otherwise get a `ready` verdict nobody measured. Throwing
 * was considered and rejected: a shared derivation that throws turns a caller's
 * degraded-but-safe path into a 500 at a print station. `unknown` costs a live
 * probe, which is always safe and is what every other unreadable case here
 * already degrades to.
 */
function noFormatsRequested(formats) {
    return formats.length === 0;
}
/** True when ANY applicable side is unknown in ANY of the requested formats.
 *  BOTH stations fail closed on this — DTG since BLU-820, DTF always.
 *  An EMPTY `formats` set is `true` — see {@link noFormatsRequested}. */
export function manifestIsIndeterminate(m, formats) {
    if (noFormatsRequested(formats))
        return true;
    return applicableSides(m).some((side) => formats.some((format) => sidePresence(side, format) === 'unknown'));
}
function applicableSides(m) {
    return [m.sides.front, m.sides.back].filter((s) => s !== undefined);
}
/** A stored proof is a claim about the PAST. Callers must apply a TTL and fall
 *  back to a live probe when it lapses — never treat a manifest as timeless. */
export function manifestIsFresh(m, nowMs, maxAgeMs) {
    return nowMs - Date.parse(m.resolvedAt) <= maxAgeMs;
}
/**
 * D-31 — THE STATION-SIDE COVERAGE QUESTION, and the ONE implementation of it.
 *
 * A side counts as COVERED when it is proved `present` in AT LEAST ONE of the
 * location's needed formats. Not all of them.
 *
 * WHY AT-LEAST-ONE, given that {@link deriveManifestStatus} is stricter. The
 * two answer different questions and both answers are needed:
 *
 *  - `deriveManifestStatus` answers "is this CLAIM complete" — it is the value
 *    stored in `prod_garment_manifests.status` for ops and indexing, and a
 *    claim that never probed the png lane is genuinely incomplete.
 *  - this answers "can this FLOOR proceed on the claim". The station's
 *    printer identity is display-only and must never enter coverage: this is
 *    one screen serving a floor that may hold ten
 *    Brothers and one Kora. Requiring every needed format would stall every
 *    Brother behind the Kora's missing PNG — which is the half-printed-garment
 *    failure this subsystem already learned once (BLU-820).
 *
 * PER-DEVICE CORRECTNESS IS ENFORCED WHERE THE DEVICE IS, not here:
 * `get-print-file` resolves and checks the REQUESTING AGENT's own format, so a
 * Kora can never be handed an `.arx4` proof as if it were its file. This
 * function is deliberately not a substitute for that check and must never be
 * used as one.
 *
 * Exported so the read-time trust gate and the station's own hasFront/hasBack
 * read the SAME rule. Two copies of a coverage rule is how a gate and its call
 * site drift into disagreeing about the same garment.
 */
export function sideIsCovered(side, formats) {
    return sideCoveragePresence(side, formats) === 'present';
}
/**
 * The D-31 rule expressed TRI-STATE — what this side is, across the whole
 * needed set, in the same vocabulary a live probe answers in.
 *
 * `present` when any needed format proves it (the at-least-one rule);
 * otherwise `unknown` if ANY needed lane is unprobed or indeterminate (fail
 * closed — an unknown side must never become a not-required side); otherwise
 * `absent`, which means every needed lane was probed and every one was empty.
 *
 * THREE CALLERS, ONE RULE, deliberately: the read-time trust gate
 * ({@link deriveGateReadiness}), the print station's own hasFront/hasBack, and
 * the divergence comparison that decides whether a floor's manifests are
 * trustworthy enough to flip. That last one is why this must not be a second
 * copy — a readiness readout computed under a DIFFERENT rule than the one that
 * will serve is measuring the wrong thing, and it is the readout a human uses
 * to decide whether to turn the feature on for a floor.
 */
export function sideCoveragePresence(side, formats) {
    // An empty needed set is `unknown`, never `absent` — see
    // {@link noFormatsRequested}. This is also what makes `deriveGateReadiness`
    // fail closed on one, since it folds this function's answer.
    if (noFormatsRequested(formats))
        return 'unknown';
    const presences = formats.map((format) => sidePresence(side, format));
    if (presences.includes('present'))
        return 'present';
    if (presences.includes('unknown'))
        return 'unknown';
    return 'absent';
}
/**
 * The readiness fold the READ-TIME TRUST GATE applies, under the D-31 rule.
 *
 * Same status vocabulary and same precedence as {@link deriveManifestStatus},
 * but per side it asks {@link sideIsCovered} first. A side that is covered in
 * some needed format is settled; only an UNCOVERED side is then classified,
 * and it fails closed — any unprobed or indeterminate lane makes it
 * `indeterminate` (-> live probe), and only a side whose every needed lane was
 * probed and found empty is `absent`.
 */
export function deriveGateReadiness(m, formats) {
    const sides = applicableSides(m);
    if (sides.length === 0)
        return 'unresolved';
    let anyIndeterminate = false;
    let anyAbsent = false;
    for (const side of sides) {
        const presence = sideCoveragePresence(side, formats);
        if (presence === 'unknown')
            anyIndeterminate = true;
        else if (presence === 'absent')
            anyAbsent = true;
    }
    if (anyIndeterminate)
        return 'indeterminate';
    if (anyAbsent)
        return 'absent';
    return 'ready';
}
export function deriveManifestStatus(m, formats) {
    const sides = applicableSides(m);
    if (sides.length === 0)
        return 'unresolved';
    // Before the fold, not after: `[].includes('unknown')` is `false`, so an
    // empty needed set would fall through to `ready` for a manifest that proved
    // nothing. See {@link noFormatsRequested}.
    if (noFormatsRequested(formats))
        return 'indeterminate';
    // Folded over every applicable side x every requested format, with the v1
    // precedence preserved exactly: unknown outranks absent outranks ready. An
    // UNPROBED lane is `unknown`, so a manifest built by a Brother-only floor is
    // `indeterminate` — never `ready` — the moment a Kora is in the requested
    // set. That is the fail-closed direction: it costs a live probe, never a
    // garment printed off a proof nobody took.
    const presences = sides.flatMap((side) => formats.map((format) => sidePresence(side, format)));
    if (presences.includes('unknown'))
        return 'indeterminate';
    if (presences.includes('absent'))
        return 'absent';
    return 'ready';
}
